import * as React from "react";

import { CurrentUserContext } from "@v2/contexts/CurrentUserContext";
import { initUserBasedAnalytics } from "@v2/features/infra/analytics/init";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { userRouter } from "@v2/trpc/routes/user";
import { isPublicSharePath } from "@v2/utils/router";
import { storeToken } from "@v2/utils/store-token";
import { getTRPCErrorStatus } from "@v2/utils/trpcErrorUtils";
import { useLocation } from "react-router-dom";
import { isLocalhost } from "replo-runtime/shared/env";
import { HTTP_STATUS_CODES } from "replo-utils/lib/errors";
import {
  clearLastVisitedDashboardRoute,
  getLandingUserQueryInput,
} from "replo-utils/lib/lastVisitedDashboardIds";

export const CurrentUserProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { user, isAuthenticated, isLoading, isError } = useFetchCurrentUser();
  return (
    <CurrentUserContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        isError,
      }}
    >
      {children}
    </CurrentUserContext.Provider>
  );
};

const MAX_PYLON_INITIALIZE_ATTEMPTS = 10;

function useFetchCurrentUser() {
  const { pathname } = useLocation();
  const isOnboarding = pathname.includes("/onboarding");

  // Read once per mount so a long-lived bundle still sees the last project visit (REPL-30512).
  const [landingUserQueryInput] = React.useState(getLandingUserQueryInput);

  const {
    data: user,
    isLoading,
    isError,
    isSuccess,
    error,
  } = userRouter.get.useQuery(landingUserQueryInput);
  const userIsInitialized = React.useRef(false);

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    // Note (Noah, 2024-08-21): Only initialize the user-based analytics once
    // throughout the lifetime of the page, since we don't want to re-initialize
    // if multiple components are using this hook.
    if (isSuccess && user && !userIsInitialized.current) {
      // Logout keeps the cookie, so drop one this account cannot open before "/" redirects to it.
      if (
        user.defaultProjectId !== landingUserQueryInput.lastVisitedProjectId
      ) {
        clearLastVisitedDashboardRoute();
      }
      initUserBasedAnalytics(user);
      userIsInitialized.current = true;
    }
  }, [isSuccess, user, landingUserQueryInput]);

  // Only a rejected token warrants dropping it. The key is shared with the
  // agent app's tabs on this origin, which treat its removal as a logout, so a
  // transient backend failure here must not sign the whole browser out.
  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    if (getTRPCErrorStatus(error) === HTTP_STATUS_CODES.UNAUTHORIZED) {
      storeToken(null);
    }
  }, [error]);

  const isSupportChatEnabled =
    !isLocalhost() && !isOnboarding && !isPublicSharePath(pathname);
  const logEvent = useLogAnalytics();

  const { data: pylonIdentityData } = userRouter.getPylonIdentityHash.useQuery(
    undefined,
    {
      enabled: Boolean(user) && isSupportChatEnabled,
    },
  );

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    // This effect handles the Pylon widget script injection and configuration
    if (user && isSupportChatEnabled) {
      window.pylon = window.pylon || {
        chat_settings: {
          app_id: "efe32725-24c5-4f17-bb77-42f06cc70918",
          email: user.email,
          name: user.name ?? user.email,
        },
      };

      // Set the email hash for Pylon identity verification
      if (pylonIdentityData?.emailHash && window.pylon?.chat_settings) {
        window.pylon.chat_settings.email_hash = pylonIdentityData.emailHash;
      }

      let pylonCheckAttempts = 0;

      const configurePylon = () => {
        if (window.Pylon) {
          window.Pylon("onShow", function () {
            window.dispatchEvent(new Event("replo:pylon-chat-show"));
            logEvent("support.chat.open", {
              source: "pylon",
              userEmail: user.email,
              userName: user.name ?? user.email,
            });
          });
          window.Pylon("onHide", function () {
            window.dispatchEvent(new Event("replo:pylon-chat-hide"));
          });
        } else if (pylonCheckAttempts < MAX_PYLON_INITIALIZE_ATTEMPTS) {
          // Note (Juan, 09/11/2024): The Pylon script is loaded asynchronously, which means it might not be
          // immediately available after the script's "load" event fires. To handle this,
          // we implement a retry mechanism using setTimeout. This function will keep
          // checking for the window.Pylon object every 500ms until it becomes available.
          // This ensures that we can configure the Pylon chat as soon as it's ready,
          // without risking trying to use it before it's fully loaded.
          pylonCheckAttempts++;
          setTimeout(configurePylon, 500);
        } else {
          console.warn("Failed to load Pylon after multiple attempts");
        }
      };

      configurePylon();
    }
  }, [user, isSupportChatEnabled, logEvent, pylonIdentityData?.emailHash]);

  return {
    user: user ?? undefined,
    isAuthenticated: Boolean(user?.id),
    isLoading,
    isError,
  };
}
